import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Markdown, isShortHint, safeHref } from '../markdown';

/**
 * The info icon beside a setting's title, when it has a hint or a link. A
 * short hint shows on hover in the app's tooltip; a click opens the whole
 * hint in a popover, with the link as "Read more". Never a native `title`.
 */
export function InfoHint({ label, hint, link }: { label: string; hint?: string | null; link?: string | null }) {
  const href = safeHref(link);
  if (!hint && !href) return null;
  const trigger = (
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={`About ${label}`}
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
    </PopoverTrigger>
  );
  return (
    <Popover>
      {isShortHint(hint) ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent className="max-w-64">
            <Markdown text={hint} inline />
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <PopoverContent aria-label={label}>
        <div className="flex flex-col gap-2 text-sm leading-relaxed">
          {hint ? <Markdown text={hint} /> : null}
          {href ? (
            <a href={href} target="_blank" rel="noopener noreferrer" className="self-start text-sm font-medium underline underline-offset-2 hover:opacity-80">
              Read more
            </a>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
